import {
  ProcessDataFromClipboardParams,
  SuppressKeyboardEventParams,
} from "ag-grid-community";
import { toast } from "react-toastify";

let hasShownDeleteToast = false;
let deleteToastTimeout: NodeJS.Timeout | null = null;

/**
 * Intercepts clipboard data BEFORE pasting.
 * If more than 1 cell is being pasted, rejects the entire operation.
 */
export function processDataFromClipboardPreventMulti(
  params: ProcessDataFromClipboardParams,
) {
  const cellRanges = params.api.getCellRanges();

  const selectedCellCount =
    cellRanges?.reduce((count, range) => {
      const rowCount = range.endRow!.rowIndex - range.startRow!.rowIndex + 1;
      const colCount = range.columns.length;
      return count + rowCount * colCount;
    }, 0) ?? 0;

  if (selectedCellCount > 1) {
    toast.warning("Please edit cells individually.");
    return null; // cancel paste
  }

  return; // allow single-cell paste
}

/**
 * Suppress keyboard events for multi-cell delete/backspace.
 * Use in defaultColDef or column definition: suppressKeyboardEvent: suppressMultiCellDelete
 * Returns true to suppress the event, false to allow it.
 */
export function suppressMultiCellDelete(
  params: SuppressKeyboardEventParams,
): boolean {
  const event = params.event;
  const key = event.key;

  // Only check Delete and Backspace keys
  if (key !== "Delete" && key !== "Backspace") {
    return false; // Allow other keys
  }

  const api = params.api;
  const cellRanges = api.getCellRanges();

  if (cellRanges && cellRanges.length > 0) {
    const range = cellRanges[0];
    const startRowIndex = range.startRow?.rowIndex ?? 0;
    const endRowIndex = range.endRow?.rowIndex ?? 0;
    const rowCount = Math.abs(endRowIndex - startRowIndex) + 1;
    const colCount = range.columns.length;

    if (rowCount > 1 || colCount > 1) {
      // Show toast only once per delete attempt
      if (!hasShownDeleteToast) {
        hasShownDeleteToast = true;
        toast.warning("Please edit cells individually.");

        // Reset after a short delay
        if (deleteToastTimeout) clearTimeout(deleteToastTimeout);
        deleteToastTimeout = setTimeout(() => {
          hasShownDeleteToast = false;
        }, 500);
      }

      return true; // Suppress the delete
    }
  }

  return false; // Allow single-cell delete - Will most likely NOT get hit as our cells have cell editors that will be getting used when clicking inot the cell.
}
